import React, { useEffect, useState } from "react";
import Select, { MultiValue } from "react-select";
import apiRequestCall from "../../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../../router/backendComponents/admin/all_routes";
import Loader from "../../../../../formHelper/Loader";
import Pagination from "../../../../../formHelper/pagination";
import exportApiCall from "../../../../../apiHelper/export";
import Export from "../../../../../formHelper/export";

type YearOption = { value: string; label: string };
type MonthOption = { value: string; label: string };
type QuarterOption = { value: string; label: string };

const generateFYOptions = (count: number): YearOption[] => {
  const currentYear = new Date().getFullYear();
  const fyStart =
    new Date().getMonth() + 1 >= 4 ? currentYear : currentYear - 1;
  const startYear = fyStart - 1;

  return Array.from({ length: count }, (_, i) => {
    const from = startYear + i;
    const to = from + 1;
    const label = `${from}-${to}`;
    return { value: label, label };
  });
};

const monthOptions: MonthOption[] = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const quarterOptions: QuarterOption[] = [
  { value: "Q1", label: "Q1 (Apr - Jun)" },
  { value: "Q2", label: "Q2 (Jul - Sep)" },
  { value: "Q3", label: "Q3 (Oct - Dec)" },
  { value: "Q4", label: "Q4 (Jan - Mar)" },
];

const selectStyles = {};
const formatCellValue = (head: string, value: any) => {
  if (value !== null && value !== undefined) {
    if (head === "growth") return `${value}%`;
    if (isNaN(Number(value))) return value;
    return Number(value).toLocaleString();
  }
  return head === "growth" ? "-" : 0;
};

const DesktopTable: React.FC<{
  headers: string[];
  rows: any[];
  loading: boolean;
  totalRow: any | null;
}> = ({ headers, rows, loading, totalRow }) => {
  if (loading)
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );

  return (
    <div className="table-responsive">
      <table className="table table-bordered mb-0">
        <thead>
          <tr>
            {headers.map((head) => (
              <th
                key={`head-${head}`}
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {head === "growth"
                  ? "Growth %"
                  : head.charAt(0).toUpperCase() + head.slice(1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center">
                No data available
              </td>
            </tr>
          ) : (
            <>
              {rows.map((row, idx) => (
                <tr key={`row-${idx}`}>
                  {headers.map((head) => (
                    <td
                      key={`cell-${idx}-${head}`}
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        ...(head === "growth"
                          ? {
                              color:
                                Number(row[head]) > 0
                                  ? "green"
                                  : Number(row[head]) < 0
                                  ? "red"
                                  : "black",
                            }
                          : {}),
                      }}
                    >
                      {formatCellValue(head, row[head])}
                    </td>
                  ))}
                </tr>
              ))}

              {totalRow && (
                <tr className="fw-bold" style={{ backgroundColor: "#f1f3f4" }}>
                  {headers.map((head) => (
                    <td
                      key={`total-${head}`}
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                    >
                      {formatCellValue(head, totalRow[head])}
                    </td>
                  ))}
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Mobile card variants combined in one small component
const MobileCards: React.FC<{
  headers: string[];
  rows: any[];
  loading: boolean;
  totalRow: any | null;
  keyField: string;
}> = ({ headers, rows, loading, totalRow, keyField }) => {
  if (loading)
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  if (rows.length === 0)
    return <div className="text-center py-3">No data available</div>;

  const renderGrowth = (val: any) => {
    if (val === null || val === undefined || val === "")
      return <span className="growth neutral">-</span>;
    const num = Number(val);
    const cls =
      num > 0
        ? "growth positive"
        : num < 0
        ? "growth negative"
        : "growth neutral";
    return <span className={cls}>{isNaN(num) ? String(val) : `${num}%`}</span>;
  };

  return (
    <div className="mobile-cards-wrapper">
      {rows.map((row: any, idx: number) => (
        <div className="card mobile-card mb-2" key={`${keyField}-${idx}`}>
          <div className="card-body p-2">
            {/* Two-column compact layout for wider phones, stacked for very small */}
            <div className="d-flex flex-column">
              {headers.map((head) => (
                <div
                  className="d-flex justify-content-between align-items-start mb-1"
                  key={`mcell-${idx}-${head}`}
                >
                  <div
                    className="mobile-label small text-muted text-truncate"
                    style={{ maxWidth: "45%" }}
                  >
                    {head === "growth"
                      ? "Growth %"
                      : head.charAt(0).toUpperCase() + head.slice(1)}
                  </div>
                  <div
                    className="mobile-value small text-end text-truncate"
                    style={{ maxWidth: "55%" }}
                  >
                    {head === "growth"
                      ? renderGrowth(row[head])
                      : formatCellValue(head, row[head])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {totalRow && (
        <div className="card mobile-card total-card mb-2">
          <div className="card-body p-2 fw-bold small">
            {headers.map((head) => (
              <div
                className="d-flex justify-content-between mb-1"
                key={`total-mobile-${head}`}
              >
                <div
                  className="mobile-label text-truncate"
                  style={{ maxWidth: "45%" }}
                >
                  {head === "growth"
                    ? "Growth %"
                    : head.charAt(0).toUpperCase() + head.slice(1)}
                </div>
                <div
                  className="mobile-value text-end text-truncate"
                  style={{ maxWidth: "55%" }}
                >
                  {formatCellValue(head, totalRow[head])}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PerformanceReport: React.FC = () => {
  const backendRoute = all_routes.backend;
  const yearOptions: YearOption[] = generateFYOptions(5);

  // Branch
  const [branchHeaders, setBranchHeaders] = useState<string[]>([]);
  const [branchSummary, setBranchSummary] = useState<any[]>([]);
  const [branchTotal, setBranchTotal] = useState<any | null>(null);
  const [branchYears, setBranchYears] = useState<MultiValue<YearOption>>([]);
  const [branchMonth, setBranchMonth] = useState<MonthOption | null>(null);
  const [branchQuarter, setBranchQuarter] = useState<QuarterOption | null>(
    null
  );
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchPage, setBranchPage] = useState(1);
  const [branchPerPage, setBranchPerPage] = useState(5);
  const [branchTotalItems, setBranchTotalItems] = useState(0);

  // Principal
  const [principalHeaders, setPrincipalHeaders] = useState<string[]>([]);
  const [principalSummary, setPrincipalSummary] = useState<any[]>([]);
  const [principalTotalRow, setPrincipalTotalRow] = useState<any | null>(null);
  const [principalYears, setPrincipalYears] = useState<MultiValue<YearOption>>(
    []
  );
  const [principalMonth, setPrincipalMonth] = useState<MonthOption | null>(
    null
  );
  const [principalQuarter, setPrincipalQuarter] =
    useState<QuarterOption | null>(null);
  const [principalLoading, setPrincipalLoading] = useState(false);
  const [principalPage, setPrincipalPage] = useState(1);
  const [principalPerPage, setPrincipalPerPage] = useState(5);
  const [principalTotalItems, setPrincipalTotalItems] = useState(0);

  // Customer
  const [customerHeaders, setCustomerHeaders] = useState<string[]>([]);
  const [customerSummary, setCustomerSummary] = useState<any[]>([]);
  const [customerTotalRow, setCustomerTotalRow] = useState<any | null>(null);
  const [customerYears, setCustomerYears] = useState<MultiValue<YearOption>>(
    []
  );
  const [customerMonth, setCustomerMonth] = useState<MonthOption | null>(null);
  const [customerQuarter, setCustomerQuarter] = useState<QuarterOption | null>(
    null
  );
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPerPage, setCustomerPerPage] = useState(5);
  const [customerTotalItems, setCustomerTotalItems] = useState(0);

  // Category
  const [categoryHeaders, setCategoryHeaders] = useState<string[]>([]);
  const [categorySummary, setCategorySummary] = useState<any[]>([]);
  const [categoryTotalRow, setCategoryTotalRow] = useState<any | null>(null);
  const [categoryYears, setCategotryYears] = useState<MultiValue<YearOption>>(
    []
  );
  const [categoryMonth, setCategoryMonth] = useState<MonthOption | null>(null);
  const [categoryQuarter, setCategoryQuarter] = useState<QuarterOption | null>(
    null
  );
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPerPage, setCategoryPerPage] = useState(5);
  const [categoryTotalItems, setCategoryTotalItems] = useState(0);

  // Authorised
  const [authorisedHeaders, setAuthorisedHeaders] = useState<string[]>([]);
  const [authorisedSummary, setAuthorisedSummary] = useState<any[]>([]);
  const [authorisedTotalRow, setAuthorisedTotalRow] = useState<any | null>(
    null
  );
  const [authorisedYears, setAuthorisedYears] = useState<
    MultiValue<YearOption>
  >([]);
  const [authorisedMonth, setAuthorisedMonth] = useState<MonthOption | null>(
    null
  );
  const [authorisedQuarter, setAuthorisedQuarter] =
    useState<QuarterOption | null>(null);
  const [authorisedLoading, setAuthorisedLoading] = useState(false);
  const [authorisedPage, setAuthorisedPage] = useState(1);
  const [authorisedPerPage, setAuthorisedPerPage] = useState(5);
  const [authorisedTotalItems, setAuthorisedTotalItems] = useState(0);

  const [exportingBranch, setExportingBranch] = useState(false);
  const [downloadingBranch, setDownloadingBranch] = useState(false);

  const [exportingPrincipal, setExportingPrincipal] = useState(false);
  const [downloadingPrincipal, setDownloadingPrincipal] = useState(false);

  const [exportingCustomer, setExportingCustomer] = useState(false);
  const [downloadingCustomer, setDownloadingCustomer] = useState(false);

  const [exportingCatgory, setExportingCategory] = useState(false);
  const [downloadingCategory, setDownloadingCategory] = useState(false);

  const [exportingAuthorised, setExportingAuthorised] = useState(false);
  const [downloadingAuthorised, setDownloadingAuthorised] = useState(false);

  // Fetch functions (kept behavior exactly same)
  const fetchBranchSummary = async () => {
    setBranchLoading(true);
    try {
      const payload: any = {};
      if (branchYears.length > 0)
        payload.years = branchYears.map((y) => y.value);
      if (branchMonth) payload.month = branchMonth.value;
      if (branchQuarter) payload.quarter = branchQuarter.value;

      const res = await apiRequestCall(
        "post",
        `${backendRoute.branchSummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
        { data: payload }
      );
      const resp = res?.data ?? {};
      if (resp?.status) {
        setBranchHeaders(resp.data?.headers || []);
        setBranchSummary(resp.data?.pagination?.data || []);
        setBranchTotalItems(resp.data?.pagination?.total || 0);
        setBranchTotal(resp.data?.total || null);
      }
    } finally {
      setBranchLoading(false);
    }
  };

  const fetchPrincipalSummary = async () => {
    setPrincipalLoading(true);
    try {
      const payload: any = {};
      if (principalYears.length > 0)
        payload.years = principalYears.map((y) => y.value);
      if (principalMonth) payload.month = principalMonth.value;
      if (principalQuarter) payload.quarter = principalQuarter.value;

      const res = await apiRequestCall(
        "post",
        `${backendRoute.principalSummaryReport}?page=${principalPage}&per_page=${principalPerPage}`,
        { data: payload }
      );
      const resp = res?.data ?? {};
      if (resp?.status) {
        setPrincipalHeaders(resp.data?.headers || []);
        setPrincipalSummary(resp.data?.pagination?.data || []);
        setPrincipalTotalItems(resp.data?.pagination?.total || 0);
        setPrincipalTotalRow(resp.data?.total || null);
      }
    } finally {
      setPrincipalLoading(false);
    }
  };

  const fetchCustomerSummary = async () => {
    setCustomerLoading(true);
    try {
      const payload: any = {};
      if (customerYears.length > 0)
        payload.years = customerYears.map((y) => y.value);
      if (customerMonth) payload.month = customerMonth.value;
      if (customerQuarter) payload.quarter = customerQuarter.value;

      const res = await apiRequestCall(
        "post",
        `${backendRoute.customerSummaryReport}?page=${customerPage}&per_page=${customerPerPage}`,
        { data: payload }
      );
      const resp = res?.data ?? {};
      if (resp?.status) {
        setCustomerHeaders(resp.data?.headers || []);
        setCustomerSummary(resp.data?.pagination?.data || []);
        setCustomerTotalItems(resp.data?.pagination?.total || 0);
        setCustomerTotalRow(resp.data?.total || null);
      }
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchCategorySummary = async () => {
    setCategoryLoading(true);
    try {
      const payload: any = {};
      if (categoryYears.length > 0)
        payload.years = categoryYears.map((y) => y.value);
      if (categoryMonth) payload.month = categoryMonth.value;
      if (categoryQuarter) payload.quarter = categoryQuarter.value;

      const res = await apiRequestCall(
        "post",
        `${backendRoute.categorySummaryReport}?page=${categoryPage}&per_page=${categoryPerPage}`,
        { data: payload }
      );
      const resp = res?.data ?? {};
      if (resp?.status) {
        setCategoryHeaders(resp.data?.headers || []);
        setCategorySummary(resp.data?.pagination?.data || []);
        setCategoryTotalItems(resp.data?.pagination?.total || 0);
        setCategoryTotalRow(resp.data?.total || null);
      }
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchAuthorisedSummary = async () => {
    setAuthorisedLoading(true);
    try {
      const payload: any = {};
      if (authorisedYears.length > 0)
        payload.years = authorisedYears.map((y) => y.value);
      if (authorisedMonth) payload.month = authorisedMonth.value;
      if (authorisedQuarter) payload.quarter = authorisedQuarter.value;

      const res = await apiRequestCall(
        "post",
        `${backendRoute.authorisedSummaryReport}?page=${authorisedPage}&per_page=${authorisedPerPage}`,
        { data: payload }
      );
      const resp = res?.data ?? {};
      if (resp?.status) {
        setAuthorisedHeaders(resp.data?.headers || []);
        setAuthorisedSummary(resp.data?.pagination?.data || []);
        setAuthorisedTotalItems(resp.data?.pagination?.total || 0);
        setAuthorisedTotalRow(resp.data?.total || null);
      }
    } finally {
      setAuthorisedLoading(false);
    }
  };

  const triggerDownload = (url: string, filename: string, filePrefix: any) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    if (filePrefix === "branch-summary") {
      setDownloadingBranch(true);
      setTimeout(() => setDownloadingBranch(false), 3000);
      setExportingBranch(false);
    }

    if (filePrefix === "principal-summary") {
      setDownloadingPrincipal(true);
      setTimeout(() => setDownloadingPrincipal(false), 3000);
      setExportingPrincipal(false);
    }

    if (filePrefix === "customer-summary") {
      setDownloadingCustomer(true);
      setTimeout(() => setDownloadingCustomer(false), 3000);
      setExportingCustomer(false);
    }

    if (filePrefix === "category-summary") {
      setDownloadingCategory(true);
      setTimeout(() => setDownloadingCategory(false), 3000);
      setExportingCategory(false);
    }

    if (filePrefix === "authorised-summary") {
      setDownloadingAuthorised(true);
      setTimeout(() => setDownloadingAuthorised(false), 3000);
      setExportingAuthorised(false);
    }
  };

  const pollForFile = (filename: string, filePrefix: String) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);

          if (filePrefix === "branch-summary") {
            setExportingBranch(false);
            setDownloadingBranch(true);
          }

          if (filePrefix === "principal-summary") {
            setExportingPrincipal(false);
            setDownloadingPrincipal(true);
          }

          if (filePrefix === "customer-summary") {
            setExportingCustomer(false);
            setDownloadingCustomer(true);
          }

          if (filePrefix === "category-summary") {
            setExportingCategory(false);
            setDownloadingCategory(true);
          }
          if (filePrefix === "authorised-summary") {
            setExportingAuthorised(false);
            setDownloadingAuthorised(true);
          }

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, filePrefix);

          if (filePrefix === "branch-summary")
            setTimeout(() => setDownloadingBranch(false), 3000);

          if (filePrefix === "principal-summary")
            setTimeout(() => setDownloadingPrincipal(false), 3000);

          if (filePrefix === "customer-summary")
            setTimeout(() => setDownloadingCustomer(false), 3000);

          if (filePrefix === "category-summary")
            setTimeout(() => setDownloadingCategory(false), 3000);

          if (filePrefix === "authorised-summary")
            setTimeout(() => setDownloadingAuthorised(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExportingBranch(false);
          setExportingPrincipal(false);
          setExportingCustomer(false);
          setExportingCategory(false);
          setExportingAuthorised(false);

          setDownloadingBranch(false);
          setDownloadingPrincipal(false);
          setDownloadingCustomer(false);
          setDownloadingCategory(false);
          setDownloadingAuthorised(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExportingBranch(false);
          setExportingPrincipal(false);
          setExportingCustomer(false);
          setExportingCategory(false);
          setExportingAuthorised(false);

          setDownloadingBranch(false);
          setDownloadingPrincipal(false);
          setDownloadingCustomer(false);
          setDownloadingCategory(false);
          setDownloadingAuthorised(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportSummaryFile = async (
    route: string,
    payload: any,
    filePrefix: string
  ) => {
    try {
      if (filePrefix === "branch-summary") {
        setExportingBranch(true);
        setDownloadingBranch(false);
      }
      if (filePrefix === "principal-summary") {
        setExportingPrincipal(true);
        setDownloadingPrincipal(false);
      }
      if (filePrefix === "customer-summary") {
        setExportingCustomer(true);
        setDownloadingCustomer(false);
      }
      if (filePrefix === "category-summary") {
        setExportingCategory(true);
        setDownloadingCategory(false);
      }
      if (filePrefix === "authorised-summary") {
        setExportingAuthorised(true);
        setDownloadingAuthorised(false);
      }

      const result = await exportApiCall("post", route, {
        data: payload,
      });

      const filename = result?.data?.file;
      if (!filename) {
        setExportingBranch(false);
        setExportingPrincipal(false);
        setExportingCustomer(false);
        setExportingCategory(false);
        setExportingAuthorised(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename, filePrefix);
    } catch (error) {
      console.error("Export failed:", error);
      setExportingBranch(false);
      setExportingPrincipal(false);
      setExportingCustomer(false);
      setExportingCategory(false);
      setExportingAuthorised(false);

      setDownloadingBranch(false);
      setDownloadingPrincipal(false);
      setDownloadingCustomer(false);
      setDownloadingCategory(false);
      setDownloadingAuthorised(false);

      alert("Export failed. Please try again.");
    }
  };

  const buildBranchPayload = () => {
    const payload: any = {};

    if (branchYears.length > 0) payload.years = branchYears.map((y) => y.value);
    if (branchMonth) payload.month = branchMonth.value;
    if (branchQuarter) payload.quarter = branchQuarter.value;
    payload.download = true;
    return payload;
  };

  const buildPrincipalPayload = () => {
    const payload: any = {};

    if (branchYears.length > 0) payload.years = branchYears.map((y) => y.value);

    if (branchMonth) payload.month = branchMonth.value;
    if (branchQuarter) payload.quarter = branchQuarter.value;
    payload.download = true;
    return payload;
  };

  const buildCustomerPayload = () => {
    const payload: any = {};
    if (branchYears.length > 0) payload.years = branchYears.map((y) => y.value);

    if (branchMonth) payload.month = branchMonth.value;
    if (branchQuarter) payload.quarter = branchQuarter.value;
    payload.download = true;
    return payload;
  };

  const buildAuthorisedPayload = () => {
    const payload: any = {};
    if (branchYears.length > 0) payload.years = branchYears.map((y) => y.value);

    if (branchMonth) payload.month = branchMonth.value;
    if (branchQuarter) payload.quarter = branchQuarter.value;
    payload.download = true;
    return payload;
  };

  const buildCategoryPayload = () => {
    const payload: any = {};
    if (branchYears.length > 0) payload.years = branchYears.map((y) => y.value);

    if (branchMonth) payload.month = branchMonth.value;
    if (branchQuarter) payload.quarter = branchQuarter.value;
    payload.download = true;
    return payload;
  };

  const exportBranchSummary = () => {
    exportSummaryFile(
      `${backendRoute.branchSummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
      buildBranchPayload(),
      "branch-summary"
    );
  };

  const exportPrincipalSummary = () => {
    exportSummaryFile(
      `${backendRoute.principalSummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
      buildPrincipalPayload(),
      "principal-summary"
    );
  };

  const exportCustomerSummary = () => {
    exportSummaryFile(
      `${backendRoute.customerSummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
      buildCustomerPayload(),
      "customer-summary"
    );
  };

  const exportCategorySummary = () => {
    exportSummaryFile(
      `${backendRoute.categorySummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
      buildCategoryPayload(),
      "category-summary"
    );
  };

  const exportAuthorisedSummary = () => {
    exportSummaryFile(
      `${backendRoute.authorisedSummaryReport}?page=${branchPage}&per_page=${branchPerPage}`,
      buildAuthorisedPayload(),
      "authorised-summary"
    );
  };

  // Effects
  useEffect(() => {
    fetchBranchSummary();
  }, [branchYears, branchMonth, branchQuarter, branchPage, branchPerPage]);
  useEffect(() => {
    fetchPrincipalSummary();
  }, [
    principalYears,
    principalMonth,
    principalQuarter,
    principalPage,
    principalPerPage,
  ]);
  useEffect(() => {
    fetchCustomerSummary();
  }, [
    customerYears,
    customerMonth,
    customerQuarter,
    customerPage,
    customerPerPage,
  ]);
  useEffect(() => {
    fetchCategorySummary();
  }, [
    categoryYears,
    categoryMonth,
    categoryQuarter,
    categoryPage,
    categoryPerPage,
  ]);
  useEffect(() => {
    fetchAuthorisedSummary();
  }, [
    authorisedYears,
    authorisedMonth,
    authorisedQuarter,
    authorisedPage,
    authorisedPerPage,
  ]);

  // Inject styles once (safe, idempotent)

  useEffect(() => {
    if (document.getElementById("perf-report-final-styles")) return;

    const css = `
      /* Base (scoped with .page-wrapper for specificity) */
      .page-wrapper .mobile-card {
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.06);
        background: #fff;
        color: #212529;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        transition: background-color .2s ease, color .2s ease, box-shadow .2s ease;
      }
      .page-wrapper .mobile-card .mobile-label { color: #6c757d; font-weight: 600; }
      .page-wrapper .mobile-card .mobile-value { color: #212529; }
      .page-wrapper .mobile-card .growth { font-weight: 700; }
      .page-wrapper .mobile-card .growth.positive { color: #198754; }
      .page-wrapper .mobile-card .growth.negative { color: #dc3545; }
  
      /* Mobile defaults (light) */
      @media (max-width: 575.98px) {
        .page-wrapper .mobile-card .card-body {
          padding: .5rem !important;
          border-radius: 10px;
          background: #fff !important;
        }
        .page-wrapper .mobile-card .mobile-label { font-size: .72rem; color: #6c757d !important; }
        .page-wrapper .mobile-card .mobile-value { font-size: .82rem; color: #212529 !important; }
      }
  
      /* Mobile + system dark (prefers-color-scheme) */
      @media (max-width: 575.98px) and (prefers-color-scheme: dark) {
        .page-wrapper .mobile-card .card-body { background: #071022 !important; }
        .page-wrapper .mobile-card .mobile-label { color: #e6eef8 !important; }
        .page-wrapper .mobile-card .mobile-value { color: #cbd5e1 !important; }
        .page-wrapper .mobile-card {
          background: #1a1f2b !important;
          color: #f1f3f5 !important;
          border: 1px solid rgba(255,255,255,0.08) !important;
          box-shadow: 0 6px 18px rgba(2,6,23,0.6) !important;
        }
      }
  
      /* Manual theme toggle fallbacks (desktop & mobile) */
      [data-theme="dark"] .page-wrapper .mobile-card,
      body.theme-dark .page-wrapper .mobile-card,
      html.theme-dark .page-wrapper .mobile-card {
        background: #1a1f2b !important;
        color: #f1f3f5 !important;
        border: 1px solid rgba(255,255,255,0.08) !important;
        box-shadow: 0 6px 18px rgba(2,6,23,0.6) !important;
      }
  
      [data-theme="dark"] .page-wrapper .mobile-card .card-body,
      body.theme-dark .page-wrapper .mobile-card .card-body,
      html.theme-dark .page-wrapper .mobile-card .card-body {
        background: #071022 !important;
      }
  
      [data-theme="dark"] .page-wrapper .mobile-card .mobile-label,
      body.theme-dark .page-wrapper .mobile-card .mobile-label,
      html.theme-dark .page-wrapper .mobile-card .mobile-label {
        color: #e6eef8 !important;
      }
  
      [data-theme="dark"] .page-wrapper .mobile-card .mobile-value,
      body.theme-dark .page-wrapper .mobile-card .mobile-value,
      html.theme-dark .page-wrapper .mobile-card .mobile-value {
        color: #cbd5e1 !important;
      }
  
      /* Optional: make manual dark mode respect mobile-only adjustments */
      @media (max-width: 575.98px) {
        [data-theme="dark"] .page-wrapper .mobile-card .card-body,
        body.theme-dark .page-wrapper .mobile-card .card-body,
        html.theme-dark .page-wrapper .mobile-card .card-body {
          padding: .5rem !important;
          border-radius: 10px;
        }
        [data-theme="dark"] .page-wrapper .mobile-card .mobile-label,
        body.theme-dark .page-wrapper .mobile-card .mobile-label,
        html.theme-dark .page-wrapper .mobile-card .mobile-label {
          font-size: .72rem !important;
        }
        [data-theme="dark"] .page-wrapper .mobile-card .mobile-value,
        body.theme-dark .page-wrapper .mobile-card .mobile-value,
        html.theme-dark .page-wrapper .mobile-card .mobile-value {
          font-size: .82rem !important;
        }
      }
    `;

    const style = document.createElement("style");
    style.id = "perf-report-final-styles";
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);

    // optional: cleanup if component unmounts and you want to remove the style
    return () => {
      const el = document.getElementById("perf-report-final-styles");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <h3 className="page-title">Performance Reports</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Branch */}
        <div className="row">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center g-2">
                  <div className="col-12 col-md-2 mb-2">
                    <h5 className="mb-0">
                      <i className="ti ti-grip-vertical me-1" /> Branch
                    </h5>
                  </div>

                  <div className="col-12 col-md-12 mb-0">
                    <Select<YearOption, true>
                      isMulti
                      options={yearOptions}
                      value={branchYears}
                      onChange={setBranchYears}
                      placeholder="Years"
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <Select<MonthOption, false>
                      options={monthOptions}
                      value={branchMonth}
                      onChange={setBranchMonth}
                      placeholder="Month"
                      isClearable
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <Select<QuarterOption, false>
                      options={quarterOptions}
                      value={branchQuarter}
                      onChange={setBranchQuarter}
                      placeholder="Quarter"
                      isClearable
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <button
                      className={`btn btn-soft-warning w-100 d-flex align-items-center justify-content-center ${
                        exportingBranch || downloadingBranch ? "disabled" : ""
                      }`}
                      onClick={
                        exportingBranch || downloadingBranch
                          ? undefined
                          : exportBranchSummary
                      }
                      disabled={exportingBranch || downloadingBranch}
                    >
                      <i
                        className={`me-1 ${
                          downloadingBranch
                            ? "ti ti-download"
                            : exportingBranch
                            ? "ti ti-loader ti-spin"
                            : "ti ti-file-type-xls"
                        }`}
                      ></i>
                      <span>
                        {downloadingBranch
                          ? "Downloading..."
                          : exportingBranch
                          ? "Generating..."
                          : "Export"}
                      </span>
                    </button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <DesktopTable
                    headers={branchHeaders}
                    rows={branchSummary}
                    loading={branchLoading}
                    totalRow={branchTotal}
                  />
                </div>
                <div className="d-block d-md-none">
                  <MobileCards
                    headers={branchHeaders}
                    rows={branchSummary}
                    loading={branchLoading}
                    totalRow={branchTotal}
                    keyField="branch"
                  />
                </div>
                <Pagination
                  currentPage={branchPage}
                  perPage={branchPerPage}
                  totalItems={branchTotalItems}
                  onPageChange={setBranchPage}
                  onPerPageChange={(size: any) => {
                    setBranchPerPage(size);
                    setBranchPage(1);
                  }}
                  perPageOptions={[
                    { value: 5, label: "5" },
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Principal */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-md-2 mb-2">
                    <h5 className="mb-0">
                      <i className="ti ti-grip-vertical me-1" /> Principal
                    </h5>
                  </div>
                  <div className="col-12 col-md-12 mb-0">
                    <Select<YearOption, true>
                      isMulti
                      options={yearOptions}
                      value={principalYears}
                      onChange={setPrincipalYears}
                      placeholder="Years"
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<MonthOption, false>
                      options={monthOptions}
                      value={principalMonth}
                      onChange={setPrincipalMonth}
                      placeholder="Month"
                      isClearable
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<QuarterOption, false>
                      options={quarterOptions}
                      value={principalQuarter}
                      onChange={setPrincipalQuarter}
                      placeholder="Quarter"
                      isClearable
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <button
                      className={`btn btn-soft-warning w-100 d-flex align-items-center justify-content-center ${
                        exportingPrincipal || downloadingPrincipal
                          ? "disabled"
                          : ""
                      }`}
                      onClick={
                        exportingPrincipal || downloadingPrincipal
                          ? undefined
                          : exportPrincipalSummary
                      }
                      disabled={exportingPrincipal || downloadingPrincipal}
                    >
                      <i
                        className={`me-1 ${
                          downloadingPrincipal
                            ? "ti ti-download"
                            : exportingPrincipal
                            ? "ti ti-loader ti-spin"
                            : "ti ti-file-type-xls"
                        }`}
                      ></i>
                      <span>
                        {downloadingPrincipal
                          ? "Downloading..."
                          : exportingPrincipal
                          ? "Generating..."
                          : "Export"}
                      </span>
                    </button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <DesktopTable
                    headers={principalHeaders}
                    rows={principalSummary}
                    loading={principalLoading}
                    totalRow={principalTotalRow}
                  />
                </div>
                <div className="d-block d-md-none">
                  <MobileCards
                    headers={principalHeaders}
                    rows={principalSummary}
                    loading={principalLoading}
                    totalRow={principalTotalRow}
                    keyField="principal"
                  />
                </div>
              </div>

              <Pagination
                currentPage={principalPage}
                perPage={principalPerPage}
                totalItems={principalTotalItems}
                onPageChange={setPrincipalPage}
                onPerPageChange={(size: any) => {
                  setPrincipalPerPage(size);
                  setPrincipalPage(1);
                }}
                perPageOptions={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Customer / Category */}
        <div className="row">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-md-2 mb-2">
                    <h5 className="mb-0">
                      <i className="ti ti-grip-vertical me-1" /> Customer
                    </h5>
                  </div>
                  <div className="col-12 col-md-12 mb-0">
                    <Select<YearOption, true>
                      isMulti
                      options={yearOptions}
                      value={customerYears}
                      onChange={setCustomerYears}
                      placeholder="Years"
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<MonthOption, false>
                      options={monthOptions}
                      value={customerMonth}
                      onChange={setCustomerMonth}
                      placeholder="Month"
                      isClearable
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<QuarterOption, false>
                      options={quarterOptions}
                      value={customerQuarter}
                      onChange={setCustomerQuarter}
                      placeholder="Quarter"
                      isClearable
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <button
                      className={`btn btn-soft-warning w-100 d-flex align-items-center justify-content-center ${
                        exportingCustomer || downloadingCustomer
                          ? "disabled"
                          : ""
                      }`}
                      onClick={
                        exportingCustomer || downloadingCustomer
                          ? undefined
                          : exportCustomerSummary
                      }
                      disabled={exportingCustomer || downloadingCustomer}
                    >
                      <i
                        className={`me-1 ${
                          downloadingCustomer
                            ? "ti ti-download"
                            : exportingCustomer
                            ? "ti ti-loader ti-spin"
                            : "ti ti-file-type-xls"
                        }`}
                      ></i>
                      <span>
                        {downloadingCustomer
                          ? "Downloading..."
                          : exportingCustomer
                          ? "Generating..."
                          : "Export"}
                      </span>
                    </button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <DesktopTable
                    headers={customerHeaders}
                    rows={customerSummary}
                    loading={customerLoading}
                    totalRow={customerTotalRow}
                  />
                </div>
                <div className="d-block d-md-none">
                  <MobileCards
                    headers={customerHeaders}
                    rows={customerSummary}
                    loading={customerLoading}
                    totalRow={customerTotalRow}
                    keyField="customer"
                  />
                </div>
              </div>

              <Pagination
                currentPage={customerPage}
                perPage={customerPerPage}
                totalItems={customerTotalItems}
                onPageChange={setCustomerPage}
                onPerPageChange={(size: any) => {
                  setCustomerPerPage(size);
                  setCustomerPage(1);
                }}
                perPageOptions={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                ]}
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-md-2 mb-2">
                    <h5 className="mb-0">
                      <i className="ti ti-grip-vertical me-1" /> Category
                    </h5>
                  </div>
                  <div className="col-12 col-md-12 mb-0">
                    <Select<YearOption, true>
                      isMulti
                      options={yearOptions}
                      value={categoryYears}
                      onChange={setCategotryYears}
                      placeholder="Years"
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<MonthOption, false>
                      options={monthOptions}
                      value={categoryMonth}
                      onChange={setCategoryMonth}
                      placeholder="Month"
                      isClearable
                    />
                  </div>
                  <div className="col-12 col-md-4 mb-2">
                    <Select<QuarterOption, false>
                      options={quarterOptions}
                      value={categoryQuarter}
                      onChange={setCategoryQuarter}
                      placeholder="Quarter"
                      isClearable
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-2">
                    <button
                      className={`btn btn-soft-warning w-100 d-flex align-items-center justify-content-center ${
                        exportingCatgory || downloadingCategory
                          ? "disabled"
                          : ""
                      }`}
                      onClick={
                        exportingCatgory || downloadingCategory
                          ? undefined
                          : exportCategorySummary
                      }
                      disabled={exportingCatgory || downloadingCategory}
                    >
                      <i
                        className={`me-1 ${
                          downloadingCategory
                            ? "ti ti-download"
                            : exportingCatgory
                            ? "ti ti-loader ti-spin"
                            : "ti ti-file-type-xls"
                        }`}
                      ></i>
                      <span>
                        {downloadingCategory
                          ? "Downloading..."
                          : exportingCatgory
                          ? "Generating..."
                          : "Export"}
                      </span>
                    </button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <DesktopTable
                    headers={categoryHeaders}
                    rows={categorySummary}
                    loading={categoryLoading}
                    totalRow={categoryTotalRow}
                  />
                </div>
                <div className="d-block d-md-none">
                  <MobileCards
                    headers={categoryHeaders}
                    rows={categorySummary}
                    loading={categoryLoading}
                    totalRow={categoryTotalRow}
                    keyField="category"
                  />
                </div>
              </div>
              <Pagination
                currentPage={categoryPage}
                perPage={categoryPerPage}
                totalItems={categoryTotalItems}
                onPageChange={setCategoryPage}
                onPerPageChange={(size: any) => {
                  setCategoryPerPage(size);
                  setCategoryPage(1);
                }}
                perPageOptions={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Authorised full width */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="row align-items-center p-3">
                <div className="col-12 col-md-2 mb-2">
                  <h5 className="mb-0">
                    <i className="ti ti-grip-vertical me-1" /> Authorised
                  </h5>
                </div>
                <div className="col-12 col-md-5 mb-2">
                  <Select<YearOption, true>
                    isMulti
                    options={yearOptions}
                    value={authorisedYears}
                    onChange={setAuthorisedYears}
                    placeholder="Years"
                  />
                </div>
                <div className="col-12 col-md-2 mb-2">
                  <Select<MonthOption, false>
                    options={monthOptions}
                    value={authorisedMonth}
                    onChange={setAuthorisedMonth}
                    placeholder="Month"
                    isClearable
                  />
                </div>
                <div className="col-12 col-md-2 mb-2">
                  <Select<QuarterOption, false>
                    options={quarterOptions}
                    value={authorisedQuarter}
                    onChange={setAuthorisedQuarter}
                    placeholder="Quarter"
                    isClearable
                  />
                </div>

                <div className="col-12 col-md-1 mb-2">
                  <button
                    className={`btn btn-soft-warning w-100 d-flex align-items-center justify-content-center ${
                      exportingAuthorised || downloadingAuthorised
                        ? "disabled"
                        : ""
                    }`}
                    onClick={
                      exportingAuthorised || downloadingAuthorised
                        ? undefined
                        : exportAuthorisedSummary
                    }
                    disabled={exportingAuthorised || downloadingAuthorised}
                  >
                    <i
                      className={`me-1 ${
                        downloadingAuthorised
                          ? "ti ti-download"
                          : exportingAuthorised
                          ? "ti ti-loader ti-spin"
                          : "ti ti-file-type-xls"
                      }`}
                    ></i>
                    <span>
                      {downloadingAuthorised
                        ? "Downloading..."
                        : exportingAuthorised
                        ? "Generating..."
                        : "Export"}
                    </span>
                  </button>
                </div>
                <div className="d-none d-md-block">
                  <DesktopTable
                    headers={authorisedHeaders}
                    rows={authorisedSummary}
                    loading={authorisedLoading}
                    totalRow={authorisedTotalRow}
                  />
                </div>
                <div className="d-block d-md-none">
                  <MobileCards
                    headers={authorisedHeaders}
                    rows={authorisedSummary}
                    loading={authorisedLoading}
                    totalRow={authorisedTotalRow}
                    keyField="authorised"
                  />
                </div>
              </div>
              <Pagination
                currentPage={authorisedPage}
                perPage={authorisedPerPage}
                totalItems={authorisedTotalItems}
                onPageChange={setAuthorisedPage}
                onPerPageChange={(size: any) => {
                  setAuthorisedPerPage(size);
                  setAuthorisedPage(1);
                }}
                perPageOptions={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceReport;
